import { useNavigate, useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";
import classes from "./QuestionsReview.module.css";
import util from "../../styles/utilities.module.css";
import "../../style.css";
import { useSelector, useDispatch } from "react-redux";
import ProgressBarWithCircles from "../Layout/ProgressBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faCommentDots,
  faPaperPlane,
} from "@fortawesome/free-regular-svg-icons";
import ActionButton from "../UI/ActionButton/ActionButton";

import {
  selectFormData,
  selectMcQuestions,
  selectFfQuestions,
  selectTfQuestions,
  selectFbQuestions,
  selectFormType,
  editFfQuestion,
  editMcQuestion,
  editTfQuestion,
  editFbQuestion,
  setMcQuestions,
  setFfQuestions,
  setTfQuestions,
  setFbQuestions,
  selectTextID,
  selectQuizID,
  setDefaultValues,
  selectReadingPassage,
  setReadingPassage,
  selectTextImages,
} from "../../slices/form";
import CommentField from "../../common/Comment/CommentField";
import {
  useFeedbackMutation,
  useGenerateMoreQuestionsMutation,
  useSaveExportMutation,
  useAppUsageLogger,
} from "../../slices/authApi";
import ModalWrapper from "../UI/modal_wrapper/modal_wrapper";
import {
  getFormattedFFQuestions,
  getFormattedMCQuestions,
  getFormattedTFQuestions,
  getFormattedFBQuestions,
} from "../../utils/getFormattedQuestions";
import ExportModal from "../UI/export_modal/export_modal";
import ExportExtraInfoModal from "../UI/export_extra_info_modal/export_extra_info_modal";
import DoneIcon from "@mui/icons-material/Done";
import Spinner from "../UI/spinner/spinner";
import SignInInfoModal from "../UI/signin_info_modal/signin_info_modal";
import openGoogleLogin from "../../common/openGoogleLogin";
import boldTextDetector from "../../utils/boldTextDetector";
import ErrorModal from "../UI/error_modal/error_modal";
import { writeFile, utils } from "xlsx";

const DEFAULT_GOOGLE_EXTRA_INFO = {
  quiz_feature: true,
  point_value_mc_questions: 1,
  point_value_ff_questions: 5,
  point_value_tf_questions: 1,
  point_value_fb_questions: 1,
  release_grade: "Later, after manual review",
  students_can_see: {
    missed_question: true,
    correct_answer: true,
    point_values: true,
  },
};

const feedbackPlaceholder =
  "(Optional) Your feedback matters! Use this space to share your thoughts or suggestions, comment on something you really liked, let us know about an issue you ran into, tell us how you are using ClassGenius, or fill us in on a feature you would like to see added or changed.";
const afterSentFeedbackPlaceHolder =
  "You're awesome! We appreciate your feedback. Your thoughts and ideas help us make our product better for everyone.";

function QuestionsReview() {
  const location = useLocation();
  const { uploadedFileData, extractedText } = location.state || {}; // Ensure to default to an empty object
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [feedback] = useFeedbackMutation();
  const [saveExport, { isLoading: isSaveExportLoading }] =
    useSaveExportMutation();
  const [generateMoreQuestions, { isLoading: isGenerateMoreQuestionsLoading }] =
    useGenerateMoreQuestionsMutation();

  const logAppUse = useAppUsageLogger(); // Use the custom hook

  const textID = useSelector(selectTextID);
  const quizID = useSelector(selectQuizID);

  const formData = useSelector(selectFormData);
  const mcQuestions = useSelector(selectMcQuestions);
  const ffQuestions = useSelector(selectFfQuestions);
  const tfQuestions = useSelector(selectTfQuestions);
  const fbQuestions = useSelector(selectFbQuestions);
  const readingPassage = useSelector(selectReadingPassage);
  const images = useSelector(selectTextImages);
  
  // Use extracted text from navigation state if readingPassage is empty
  const displayText = readingPassage || extractedText || "";
  
  const [userFeedback, setUserFeedback] = useState("");

  const [editQuestion, setEditQuestion] = useState(null);
  const [updatedQuestion, setUpdatedQuestion] = useState("");
  const [updatedOptions, setUpdatedOptions] = useState("");
  const [updatedAnswer, setUpdatedAnswer] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [updatedPassage, setUpdatedPassage] = useState("");
  const [isPassageEdited, setIsPassageEdited] = useState(false);

  const [comment, setComment] = useState("");

  const [currentQestion, setCurrentQuestion] = useState("");

  const [backToHomeModal, setBackToHomeModal] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [exportExtraInfoModal, setExportExtraInfoModal] = useState(false);
  const [exportType, setExportType] = useState("");
  const [googleFormExtraInfo, setGoogleFormExtraInfo] = useState(
    DEFAULT_GOOGLE_EXTRA_INFO
  );
  const [exportedUrl, setExportedUrl] = useState("");
  const [exported, setExported] = useState(false);
  const [isFeedbackSent, setIsFeedbackSent] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  const [exportError, setExportError] = useState(false);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [fileUploadMessage, setFileUploadMessage] = useState("");
  const [submittedForSharing, setSubmittedForSharing] = useState(false);
  const [submitForSharingDescription, setSubmitForSharingDescription] = useState('');
  const toolType = useSelector(selectFormType);
  const [isPassageSelected, setIsPassageSelected] = useState(
    formData?.show_text === "Open book" || toolType === "reading"
  );

  const generateAndDownloadKahootXLSX = () => {
    const header = [
      "",
      "Question - max 120 characters",
      "Answer 1 - max 75 characters",
      "Answer 2 - max 75 characters",
      "Answer 3 - max 75 characters",
      "Answer 4 - max 75 characters",
      "Time limit (sec) – 5, 10, 20, 30, 60, 90, 120, or 240 secs",
      "Correct answer(s) - choose at least one"
    ];

    const rows = [header];
    let idx = 1;

    // Assume rows (array) and idx (number) have been initialized above
    mcQuestions?.filter((question) => question.selected).forEach((q) => {
      let options = q.options;
      if (typeof options === "string") {
        try {
          options = JSON.parse(options.replace(/'/g, '"'));
        } catch (error) {
          console.error("Failed to parse options:", q.options);
          return; // Skip this question if parsing fails
        }
      }

      // Ensure we have exactly 4 options; skip if not (e.g., 5 options)
      const optionKeys = Object.keys(options);
      if (optionKeys.length !== 4) return;

      // Build answer options in order A, B, C, D
      const answerOptions = [
        options["A"] || "",
        options["B"] || "",
        options["C"] || "",
        options["D"] || ""
      ];

      // Map answer letter to 1-based index (e.g., "D" -> "4")
      const letterMapping = { "A": "1", "B": "2", "C": "3", "D": "4" };
      const correct = letterMapping[q.answer.toUpperCase()];

      rows.push([
        idx.toString(),
        q.question,
        answerOptions[0],
        answerOptions[1],
        answerOptions[2],
        answerOptions[3],
        "30", // fixed time limit
        correct
      ]);
      idx++;
    });

    // Process true/false questions
    tfQuestions?.filter((question) => question.selected).forEach((q) => {
      const answerOptions = ["True", "False", "", ""];
      const correct = (q.answer.toLowerCase() === "true") ? "1" : "2";
      rows.push([
        idx.toString(),
        q.question,
        answerOptions[0],
        answerOptions[1],
        answerOptions[2],
        answerOptions[3],
        "30",
        correct
      ]);
      idx++;
    });

    // Process fill-in-blank questions (fb_questions)
    fbQuestions?.filter((question) => question.selected).forEach((q) => {
      rows.push([
        idx.toString(),
        q.question,
        "Option 1", // dummy answer 1
        "Option 2", // dummy answer 2
        "",         // blank answer 3
        "",         // blank answer 4
        "30",
        "1"         // correct answer marked as 1
      ]);
      idx++;
    });

    // Process free-form questions (ff_questions)
    ffQuestions?.filter((question) => question.selected).forEach((q) => {
      rows.push([
        idx.toString(),
        q.question,
        "Option 1", // dummy answer 1
        "Option 2", // dummy answer 2
        "",
        "",
        "30",
        "1"
      ]);
      idx++;
    });

    const worksheet = utils.aoa_to_sheet(rows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Quiz");
    writeFile(workbook, "quiz.xlsx");
  };

  const generateAndDownloadBlooketCSV = () => {
    const padRow = (row) => {
      const targetLength = 26;
      if (row.length >= targetLength) return row;
      return [...row, ...Array(targetLength - row.length).fill("")];
    };

    const rows = [
      padRow(["Blooket\nImport Template"]),
      padRow([
        "Question #",
        "Question Text",
        "Answer 1",
        "Answer 2",
        "Answer 3\n(Optional)",
        "Answer 4\n(Optional)",
        "Time Limit (sec)\n(Max: 300 seconds)",
        "Correct Answer(s)\n(Only include Answer #)",
      ]),
    ];

    let idx = 1;
    const letterMapping = { A: "1", B: "2", C: "3", D: "4" };

    const normalizeOptions = (rawOptions = {}) => {
      if (typeof rawOptions === "string") {
        try {
          return JSON.parse(rawOptions.replace(/'/g, '"'));
        } catch (error) {
          console.error("Failed to parse options:", rawOptions);
          return {};
        }
      }
      return rawOptions || {};
    };

    const deriveCorrectAnswers = (answer) => {
      if (!answer) return "";
      const normalized = answer.toString().toUpperCase();
      const tokens = normalized.split(/[^A-D]/).filter(Boolean);
      const mappedAnswers = tokens
        .map((token) => letterMapping[token])
        .filter(Boolean);
      if (mappedAnswers.length > 0) {
        return mappedAnswers.join(",");
      }
      return letterMapping[normalized] || "";
    };

    const buildRow = (question, answerOptions, correctAnswer) => {
      rows.push(
        padRow([
          idx.toString(),
          question.question,
          answerOptions[0] || "",
          answerOptions[1] || "",
          answerOptions[2] || "",
          answerOptions[3] || "",
          "30",
          correctAnswer || "1",
        ])
      );
      idx++;
    };

    mcQuestions
      ?.filter((question) => question.selected)
      .forEach((q) => {
        const options = normalizeOptions(q.options);
        const answerOptions = ["A", "B", "C", "D"].map(
          (key) => options?.[key] || options?.[key.toLowerCase()] || ""
        );
        if (answerOptions.every((opt) => !opt)) return;
        const correctAnswer = deriveCorrectAnswers(q.answer) || "1";
        buildRow(q, answerOptions, correctAnswer);
      });

    tfQuestions
      ?.filter((question) => question.selected)
      .forEach((q) => {
        const answerOptions = ["True", "False", "", ""];
        const correct =
          q.answer?.toString().toLowerCase() === "true" ? "1" : "2";
        buildRow(q, answerOptions, correct);
      });

    fbQuestions
      ?.filter((question) => question.selected)
      .forEach((q) => {
        const answerOptions = ["Option 1", "Option 2", "", ""];
        buildRow(q, answerOptions, "1");
      });

    ffQuestions
      ?.filter((question) => question.selected)
      .forEach((q) => {
        const answerOptions = ["Option 1", "Option 2", "", ""];
        buildRow(q, answerOptions, "1");
      });

    const worksheet = utils.aoa_to_sheet(rows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Blooket Import");
    writeFile(workbook, "blooket-import.csv", { bookType: "csv" });
  };

  const isExportForm = ["Google Forms", "Google Docs"].includes(exportType);
  // save & export endpoint
  const saveAndExportEndpoint = async (data) => {
    try {
      const { exportFormat } = data || {};

      const saveExportResponse = await saveExport({
        text_id: textID,
        quiz_id: quizID,
        export_format: exportFormat,
        ...(isExportForm
          ? {
            google_forms_extra_info: googleFormExtraInfo,
          }
          : {}),
        mc_questions: getFormattedMCQuestions(mcQuestions),
        ff_questions: getFormattedFFQuestions(ffQuestions),
        tf_questions: getFormattedTFQuestions(tfQuestions),
        fb_questions: getFormattedFBQuestions(fbQuestions),
        include_text: isPassageSelected,
        submit_for_sharing: submittedForSharing,
        ...(submittedForSharing && submitForSharingDescription
          ? { submit_for_sharing_description: submitForSharingDescription }
          : {}),
        tool_type: toolType,
        ...(isPassageSelected && isPassageEdited
          ? { revised_text: readingPassage }
          : {}),
      });
      // Reset for the next export
      setSubmittedForSharing(false);
      setSubmitForSharingDescription('');
      return saveExportResponse;
    } catch (err) {
      console.log({ err });
    }
  };

  // feedback endpoint
  const feedbackEndpointHandler = async () => {
    if (userFeedback) {
      try {
        // feedback payload
        const payload = {
          feedback: [
            {
              content_id: textID,
              category: "quiz",
              comments: userFeedback,
            },
          ],
        };
        await feedback(payload);
        setUserFeedback("");
        !isFeedbackSent && setIsFeedbackSent(true);
      } catch (err) {
        console.log({ err });
      }
    }
  };

  // const deleteQuestionHandler = () => {
  //   const updatedComment = {
  //     ...deletedQuestion.question,
  //     deleted: true,
  //     ...(comment ? { comments: comment } : {}),
  //   };
  //   if (deletedQuestion.isMCQ) {
  //     dispatch(deleteMcQuestion(updatedComment));
  //   } else {
  //     dispatch(deleteFfQuestion(updatedComment));
  //   }
  //   setComment("");
  //   setShowDeleteModal(false);
  // };

  //const questionCommentHandler = () => {
  //const updatedQuestion = {
  //  ...currentQestion.question,
  // comments: comment,
  // };
  // const questionsType = currentQestion.type === "MC";
  // const questions = questionsType ? mcQuestions : ffQuestions;
  //const updatedQuestionsComment = questions.map((question) =>
  //      question.question_id === updatedQuestion.question_id
  //   ? updatedQuestion
  //      : question
  // );
  //   dispatch(
  //   questionsType
  //   ? setMcQuestions(updatedQuestionsComment)
  //  : setFfQuestions(updatedQuestionsComment)
  //    );
  //  setComment("");
  // setCurrentQuestion("");
  // };

  const questionCommentHandler = () => {
    const updatedQuestion = {
      ...currentQestion.question,
      comments: comment,
    };
    let updatedQuestionsComment;

    // Determine the array of questions and the dispatch function based on the type
    switch (currentQestion.type) {
      case "MC":
        updatedQuestionsComment = mcQuestions.map((question) =>
          question.question_id === updatedQuestion.question_id ? updatedQuestion : question
        );
        dispatch(setMcQuestions(updatedQuestionsComment));
        break;
      case "FF":
        updatedQuestionsComment = ffQuestions.map((question) =>
          question.question_id === updatedQuestion.question_id ? updatedQuestion : question
        );
        dispatch(setFfQuestions(updatedQuestionsComment));
        break;
      case "TF":
        updatedQuestionsComment = tfQuestions.map((question) =>
          question.question_id === updatedQuestion.question_id ? updatedQuestion : question
        );
        dispatch(setTfQuestions(updatedQuestionsComment));
        break;
      case "FB":
        updatedQuestionsComment = fbQuestions.map((question) =>
          question.question_id === updatedQuestion.question_id ? updatedQuestion : question
        );
        dispatch(setFbQuestions(updatedQuestionsComment));
        break;
      default:
        console.error("Unsupported question type: " + currentQestion.type);
    }

    // Reset the comment and current question
    setComment("");
    setCurrentQuestion("");
  };

  const backToHomeHandler = async () => {
    await logAppUse("questions_review", "back_to_home_pop_up");
    await feedbackEndpointHandler();
    setBackToHomeModal(true);
  };

  const backToHomeCancelBtnHandler = async () => {
    await saveAndExportEndpoint({ exportFormat: "" });
    await logAppUse("back_to_home_pop_up", "questions_review");
    setBackToHomeModal(false);
    setIsPassageEdited(false);
  };

  const backToHomeConfirmBtnHandler = async () => {
    localStorage.removeItem("from");

    await saveAndExportEndpoint({ exportFormat: "" });
    await logAppUse("back_to_home_pop_up", "tool_selection");
    setBackToHomeModal(false);
    setIsPassageEdited(false);
    dispatch(setDefaultValues());
    navigate("/toolSelection");
  };

  const exportHandler = async () => {
    await logAppUse("questions_review", "export_pop_up");
    await feedbackEndpointHandler();
    setExportModal(true);
  };

  const backBtnExportModalHandler = async () => {
    await saveAndExportEndpoint({ exportFormat: "" });
    await logAppUse("export_pop_up", "questions_review");
    setIsPassageEdited(false);
    setExportModal(false);
  };

  const continueBtnExportModalHandler = async () => {
    if (!exportType) return;
    setExportModal(false);
    await finalStepInExportHandler();
    setIsPassageEdited(false);
  };

  const finalStepInExportHandler = async () => {
    const exportResponse = await saveAndExportEndpoint({
      exportFormat: exportType,
    });
    if (exportType === "Spreadsheet compatible with Kahoot import") {
      await logAppUse("export_pop_up", "export_kahoot");
      // For XLSX, we generate the file directly
      // and download it without opening a new tab.
      generateAndDownloadKahootXLSX();
      setExportedUrl(""); // prevents opening a new tab
      setExported(true);
      setExportType("");
    } else if (exportType === "Spreadsheet compatible with Blooket import") {
      await logAppUse("export_pop_up", "export_blooket");
      generateAndDownloadBlooketCSV();
      setExportedUrl("");
      setExported(true);
      setExportType("");
    } else {
      await logAppUse("export_pop_up", "export_url");
      if (exportResponse?.data?.status === 200) {
        // The export was successful, sow e no longer need to preserve data for a 
        // potential sign-in.to Google
        localStorage.setItem("preserveData", null);
        setExportedUrl(exportResponse?.data?.url);
      } else {
        setExportError(exportResponse?.data?.status === 500);
        setInfoModal(true);
      }
    }
  };

  const backBtnExportExtraInfoModalHandler = async () => {
    setExportExtraInfoModal(false);
    setExportModal(true);
  };

  const continueBtnExportExtraInfoModalHandler = async () => {
    setGoogleFormExtraInfo((preState) => ({
      ...preState,
      quiz_feature: "On",
    }));
    await finalStepInExportHandler();
  };

  const stepSkipBtnExportExtraInfoModalHandler = async () => {
    setGoogleFormExtraInfo((preState) => ({
      ...preState,
      quiz_feature: "Off",
    }));
    await finalStepInExportHandler();
  };

  const generateMoreQuestionsHandler = async () => {
    const moreQuestions = await generateMoreQuestions({
      quiz_id: quizID,
      mc_questions: getFormattedMCQuestions(mcQuestions),
      ff_questions: getFormattedFFQuestions(ffQuestions),
      tf_questions: getFormattedTFQuestions(tfQuestions),
      fb_questions: getFormattedFBQuestions(fbQuestions),
    });

    const previousMcQues = mcQuestions?.map((ques) => {
      return { ...ques, new: false };
    });
    const newMcQues = moreQuestions?.data?.mc_questions.map((ques) => {
      return { ...ques, new: true };
    });
    const previousFFQues = ffQuestions?.map((ques) => {
      return { ...ques, new: false };
    });
    const newFFQues = moreQuestions?.data?.ff_questions.map((ques) => {
      return { ...ques, new: true };
    });
    const previousTFQues = tfQuestions?.map((ques) => {
      return { ...ques, new: false };
    });
    const newTFQues = moreQuestions?.data?.tf_questions.map((ques) => {
      return { ...ques, new: true };
    });
    const previousFBQues = fbQuestions?.map((ques) => {
      return { ...ques, new: false };
    });
    const newFBQues = moreQuestions?.data?.fb_questions.map((ques) => {
      return { ...ques, new: true };
    });
    if (moreQuestions?.data?.quiz_id) {
      const updatedNewMCQuestions = [...previousMcQues, ...newMcQues];
      const updatedNewFFQuestions = [...previousFFQues, ...newFFQues];
      const updatedNewTFQuestions = [...previousTFQues, ...newTFQues];
      const updatedNewFBQuestions = [...previousFBQues, ...newFBQues];
      dispatch(setMcQuestions(updatedNewMCQuestions));
      dispatch(setFfQuestions(updatedNewFFQuestions));
      dispatch(setTfQuestions(updatedNewTFQuestions));
      dispatch(setFbQuestions(updatedNewFBQuestions));
    }
  };
  const getCommentFieldModal = (questionId, type) => {
    return currentQestion?.question?.question_id === questionId &&
      type === currentQestion?.type ? (
      <CommentField
        valueHandler={(comment) => setComment(comment)}
        cancelHandler={() => setCurrentQuestion("")}
        saveHandler={questionCommentHandler}
        comment={comment}
        placeholder={"share your comments about the question here"}
      />
    ) : null;
  };

  const getSelectBox = (isSelected, clickHandler) => {
    return (
      <div
        onClick={clickHandler}
        className={`${classes.selectBox} ${isSelected ? classes.selectBoxSelected : ''}`}
        style={{ marginBottom: "20px" }}
      >
        {isSelected ? (
          <DoneIcon sx={{ fontSize: "24px" }} className={classes.checkIconSelected} />
        ) : (
          <DoneIcon sx={{ fontSize: "24px" }} className={classes.checkIconHidden} />
        )}
      </div>
    );
  };

  const updateSelectedStatus = (questions, questionId) => {
    return questions.map((question) =>
      question.question_id === questionId
        ? { ...question, selected: !question.selected }
        : question
    );
  };

  const mcQuestionSelectedHandler = (questionId) => {
    const updatedMCQuestions = updateSelectedStatus(mcQuestions, questionId);
    dispatch(setMcQuestions(updatedMCQuestions));
  };

  const ffQuestionSelectedHandler = (questionId) => {
    const updatedFFQuestions = updateSelectedStatus(ffQuestions, questionId);
    dispatch(setFfQuestions(updatedFFQuestions));
  };

  const tfQuestionSelectedHandler = (questionId) => {
    const updatedTFQuestions = updateSelectedStatus(tfQuestions, questionId);
    dispatch(setTfQuestions(updatedTFQuestions));
  };

  const fbQuestionSelectedHandler = (questionId) => {
    const updatedFBQuestions = updateSelectedStatus(fbQuestions, questionId);
    dispatch(setFbQuestions(updatedFBQuestions));
  };


  const mcAnswerUpdateHandler = (e) => {
    const value = e.target.value;
    if (["A", "B", "C", "D", "E"].includes(value)) {
      setErrMsg("");
    }
    setUpdatedAnswer(value);
  };

  const tfAnswerUpdateHandler = (e) => {
    const value = e.target.value;
    if (["True", "False"].includes(value)) {
      setErrMsg("");
    }
    setUpdatedAnswer(value);
  };

  const mcQuestionUpdateHandler = (question) => {
    const valid_options = question.options.E
      ? ["A", "B", "C", "D", "E"]
      : ["A", "B", "C", "D"];
    if (!valid_options.includes(updatedAnswer)) {
      // Join the valid options array into a string, separated by commas and a space, except the last item to precede with ", or "
      const optionsStr =
        valid_options.slice(0, -1).join(", ") +
        ", or " +
        valid_options.slice(-1);
      setErrMsg(
        `You have entered an invalid answer. (valid answers: ${optionsStr})`
      );
      return;
    }
    setErrMsg("");
    dispatch(
      editMcQuestion({
        question_id: question.question_id,
        updatedQuestion: updatedQuestion,
        options: updatedOptions,
        answer: updatedAnswer,
      })
    );
    setEditQuestion(null);
  };
  const tfQuestionUpdateHandler = (question) => {
    const valid_options = ["True", "False"];
    if (!valid_options.includes(updatedAnswer)) {
      // Join the valid options array into a string, separated by commas and a space, except the last item to precede with ", or "
      const optionsStr =
        valid_options.slice(0, -1).join(", ") +
        ", or " +
        valid_options.slice(-1);
      setErrMsg(
        `You have entered an invalid answer. (valid answers: ${optionsStr})`
      );
      return;
    }
    setErrMsg("");
    dispatch(
      editTfQuestion({
        question_id: question.question_id,
        updatedQuestion: updatedQuestion,
        options: updatedOptions,
        answer: updatedAnswer,
      })
    );
    setEditQuestion(null);
  };


  const feedbackSendHandler = async () => {
    await feedbackEndpointHandler();
  };


  const updatePassageHandler = () => {
    dispatch(setReadingPassage(updatedPassage));
    setUpdatedPassage("");
    setEditQuestion("");
    setIsPassageEdited(true);
  };

  const signInInfoContinueHandler = () => {
    localStorage.setItem(
      "preserveData",
      JSON.stringify({
        sourcePage: "questionsReview",
        ffQuestions,
        mcQuestions,
        tfQuestions,
        fbQuestions,
        formData,
        readingPassage,
        quizID,
        textID,
        userFeedback
      })
    );
    openGoogleLogin();
  };

  const filteredMCQuestions = mcQuestions?.filter(
    (mcQuestion) => !mcQuestion?.deleted
  );
  const filteredFFQuestions = ffQuestions?.filter(
    (ffQuestion) => !ffQuestion?.deleted
  );

  const filteredTFQuestions = tfQuestions?.filter(
    (tfQuestion) => !tfQuestion?.deleted
  );

  const filteredFBQuestions = fbQuestions?.filter(
    (fbQuestion) => !fbQuestion?.deleted
  );


  const getIconStyle = (isTrue) => {
    return `${classes.icon} ${isTrue ? classes["icon-color"] : ""}`;
  };

  useEffect(() => {
    if (exportedUrl) {
      window.open(exportedUrl, "_blank", "noreferrer");
      setExportedUrl("");
      setExported(true);
      setExportType("");
    }
  }, [exportedUrl]);



  const checkFileUploadMessage = (fileData) => {
    let message = "";
    for (const key in fileData) {
      if (fileData[key] && fileData[key].text_truncated) {
        let used_pct = Math.floor(fileData[key].truncated_size / fileData[key].parsed_size * 100);
        message += `${fileData[key].name} - too large. Questions based on first ${used_pct}% of text.\n\n`;
      }
      if (fileData[key] && fileData[key].ocr_confidence < 80) {
        message += `${fileData[key].name} - difficult to read and may need to be rescanned if questions are unsatisfactory.\n\n`;
      }
    }
    setFileUploadMessage(message);
  };

  useEffect(() => {
    checkFileUploadMessage(uploadedFileData);
  }, [uploadedFileData]);

  // Directly use fileUploadMessage state to decide modal visibility
  useEffect(() => {
    if (fileUploadMessage) {
      setShowErrorModal(true);
    }
  }, [fileUploadMessage]); // React to changes in fileUploadMessage

  return (
    <>
      <section
        role="region"
        aria-label="Questions review"
        data-testid="questions-review-main"
        className={`${classes.pageMargins} app-main`}
      >
        {isSaveExportLoading || isGenerateMoreQuestionsLoading ? (
          <Spinner
            message={
              isSaveExportLoading && isExportForm
                ? "We are creating your exported file."
                : isGenerateMoreQuestionsLoading
                  ? "We are hard at work creating additional questions. This may take a few minutes."
                  : ""
            }
          />
        ) : null}
        <p style={{ fontSize: "larger", textAlign: "center" }}>
          <strong style={{ fontSize: "inherit" }}>Questions Review</strong>
          <br />
          Grade: {formData.grade}, Difficulty: {formData.difficulty_level}
        </p>
        <div style={{ paddingTop: "60px" }} />

        <ProgressBarWithCircles progress={exported ? 100 : 75} />

        <div className={classes["container"]}>
          <h2 className={`${classes.title} ${classes.bottomSpacer}`}>
            Review and make your selection for export:
          </h2>
          <div className={classes.formReveiwFlex}>
            {getSelectBox(isPassageSelected, () =>
              setIsPassageSelected((preState) => !preState)
            )}
            {images?.[0] && (
              <img
                src={`${images[0]}`}
                alt="Passage illustration"
                className={classes["passage-image"]}
              />
            )}
            {editQuestion !== "passage" ? (
              <div className={classes.reviewBox}>
                <div style={{ width: "92%" }}>
                  <pre className={classes.preFormattedText}>
                    {boldTextDetector(displayText)}
                  </pre>
                </div>
                <FontAwesomeIcon
                  className={`${getIconStyle(isPassageEdited)} ${classes.passageEditIcon
                    }`}
                  icon={faEdit}
                  onClick={() => {
                    setEditQuestion("passage");
                    setUpdatedPassage(displayText);
                  }}
                />
              </div>
            ) : (
              <div
                className={`${classes["paragraph-box"]} ${true ? classes["editing"] : ""
                  }`}
              >
                <textarea
                  value={updatedPassage}
                  className={`${classes["editable-passage"]} ${'isEditing'}`}
                  onChange={(e) => setUpdatedPassage(e.target.value)}
                />
                <div className={classes["icon-container"]}>
                  <button
                    type="button"
                    className={`${util.btn} ${util["btn--ghost"]} ${util["btn-sm"]}`}
                    onClick={() => {
                      setEditQuestion("");
                      setUpdatedPassage("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`${util.btn} ${util["btn--primaryGradient"]} ${util["btn-sm"]}`}
                    onClick={updatePassageHandler}
                  >
                    Update
                  </button>
                </div>
              </div>
            )}
          </div>
          {filteredMCQuestions?.length > 0 && (
            <h2 className={`${classes.title} ${classes.multipleSpacer}`}>
              <br />
              Multiple-Choice Questions
            </h2>
          )}
          {filteredMCQuestions?.map((question, index) => {
            const isEditing = editQuestion === question.question_id + "MC";
            return (
              <div
                key={question?.question_id || index}
                className={classes.question}
                data-testid="question-card"
                data-question-type="mc"
                data-question-id={question?.question_id}
              >
                {getSelectBox(question.selected, () =>
                  mcQuestionSelectedHandler(question.question_id)
                )}
                <div className={classes.fullWidth}>
                  <div className={classes["content-wrapper"]}>
                    <div
                      className={`${question.selected
                        ? classes["paragraph-box"]
                        : classes["paragraph-box-unselect"]
                        } ${isEditing ? classes["editing"] : ""}`}
                    >
                      {question.new && (
                        <span className={classes["new-badge"]}>New</span>
                      )}
                      {isEditing ? (
                        <div>
                          <textarea
                            className={`${classes["editable-text"]} isEditing`}
                            value={updatedQuestion}
                            onChange={(e) => setUpdatedQuestion(e.target.value)}
                            rows={2}
                          />
                          <textarea
                            className={`${classes["editable-text"]} isEditing`}
                            value={`A. ${updatedOptions.A}`}
                            onChange={(e) =>
                              setUpdatedOptions((prevList) => {
                                var newlist = {
                                  ...prevList,
                                  ...{ A: e.target.value.split("A. ")[1] },
                                };
                                return newlist;
                              })
                            }
                            rows={2}
                          />
                          <textarea
                            className={`${classes["editable-text"]} isEditing`}
                            value={`B. ${updatedOptions.B}`}
                            onChange={(e) =>
                              setUpdatedOptions((prevList) => {
                                var newlist = {
                                  ...prevList,
                                  ...{ B: e.target.value.split("B. ")[1] },
                                };
                                return newlist;
                              })
                            }
                            rows={2}
                          />
                          <textarea
                            className={`${classes["editable-text"]} isEditing`}
                            value={`C. ${updatedOptions.C}`}
                            onChange={(e) =>
                              setUpdatedOptions((prevList) => {
                                var newlist = {
                                  ...prevList,
                                  ...{ C: e.target.value.split("C. ")[1] },
                                };
                                return newlist;
                              })
                            }
                            rows={2}
                          />
                          <textarea
                            className={`${classes["editable-text"]} isEditing`}
                            value={`D. ${updatedOptions.D}`}
                            onChange={(e) =>
                              setUpdatedOptions((prevList) => {
                                var newlist = {
                                  ...prevList,
                                  ...{ D: e.target.value.split("D. ")[1] },
                                };
                                return newlist;
                              })
                            }
                            rows={2}
                          />
                          {question.options.E && (
                            <textarea
                              className={`${classes["editable-text"]} isEditing`}
                              value={`E. ${updatedOptions.E}`}
                              onChange={(e) =>
                                setUpdatedOptions((prevList) => {
                                  var newlist = {
                                    ...prevList,
                                    ...{ E: e.target.value.split("E. ")[1] },
                                  };
                                  return newlist;
                                })
                              }
                              rows={2}
                            />
                          )}
                          <div className={classes["icon-container"]}>
                            <p> Answer:</p>
                            <textarea
                              maxLength={1}
                              className={`${classes["editable-text"]} isEditing`}
                              value={updatedAnswer}
                              onChange={mcAnswerUpdateHandler}
                            />
                          </div>

                          <div className={classes["icon-container"]}>
                            <p className={classes["editable-text"]}>
                              {" "}
                              Category: {question.reference}
                            </p>
                          </div>

                          {errMsg ? (
                            <div className={classes.errMsg}>{errMsg}</div>
                          ) : null}

                          <div className={classes["icon-container"]}>
                            <button
                              type="button"
                              className={`${util.btn} ${util["btn--ghost"]} ${util["btn-sm"]}`}
                              onClick={() => {
                                setEditQuestion(null);
                                setErrMsg("");
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className={`${util.btn} ${util["btn--primaryGradient"]} ${util["btn-sm"]}`}
                              onClick={() => mcQuestionUpdateHandler(question)}
                            >
                              Update
                            </button>
                          </div>
                        </div>
                      ) : question.selected ? (
                        <div>
                          <div>
                            <div className={classes.alignItem}>
                              <p style={{ width: "80%" }}>
                                {boldTextDetector(question.question)}
                              </p>
                              <div>
                                <FontAwesomeIcon
                                  className={getIconStyle(question?.edited)}
                                  icon={faEdit}
                                  onClick={() => {
                                    setEditQuestion(
                                      question.question_id + "MC"
                                    );
                                    setUpdatedQuestion(question.question);
                                    setUpdatedOptions(question.options);
                                    setUpdatedAnswer(question.answer);
                                  }}
                                />
                              </div>
                            </div>
                            <div style={{ lineHeight: "25px" }}>
                              <p>A. {boldTextDetector(question.options.A)}</p>
                              <p>B. {boldTextDetector(question.options.B)}</p>
                              <p>C. {boldTextDetector(question.options.C)}</p>
                              <p>D. {boldTextDetector(question.options.D)}</p>
                              {question.options.E && (
                                <p>E. {boldTextDetector(question.options.E)}</p>
                              )}
                            </div>
                            <br />                             
                            <div>
                              <p>Answer:</p>
                              {question.answerImages && question.answerImages.length > 0 && (
                                question.answerImages.map((img, idx) => (
                                  <img
                                    key={img.id || idx}
                                    src={img.data_url}
                                    alt={img.renderer || img.id || `answer-image-${idx}`}
                                    className={classes["question-image"]}
                                  />
                                ))
                              )}
                              <p>{question.answer}</p>
                            </div>                           
                            <div className={classes.alignItem}>
                              <p>Category: {boldTextDetector(question.reference)}</p>
                              <div className={classes["icon-container"]}>
                                <FontAwesomeIcon
                                  className={getIconStyle(question?.comments)}
                                  icon={faCommentDots}
                                  onClick={() => {
                                    setCurrentQuestion({
                                      question,
                                      type: "MC",
                                    });
                                    setComment(question?.comments || "");
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className={classes.alignItem}>
                            <p style={{ width: "80%" }} className={classes.textMuted}>
                              {question.question}
                            </p>
                          </div>
                        </div>
                      )}
                      {getCommentFieldModal(question?.question_id, "MC")}
                    </div>
                  </div>
                  <div style={{ paddingTop: "20px" }} />
                </div>
              </div>
            );
          })}
          {filteredFFQuestions?.length > 0 && (
            <h2 className={`${classes.title} ${classes.multipleSpacer}`}>
              Short-Answer Questions
            </h2>
          )}
          {filteredFFQuestions?.map((question, index) => {
            const isEditing = editQuestion === question.question_id + "FF";
            return (
              <div
                key={question?.question_id || index}
                className={classes.question}
                data-testid="question-card"
                data-question-type="short"
                data-question-id={question?.question_id}
              >
                {getSelectBox(question.selected, () =>
                  ffQuestionSelectedHandler(question.question_id)
                )}
                <div className={classes.fullWidth}>
                  <div className={classes["content-wrapper"]}>
                    <div
                      className={`${question.selected
                        ? classes["paragraph-box"]
                        : classes["paragraph-box-unselect"]
                        }  ${isEditing ? classes["editing"] : ""}`}
                    >
                      {question.new && (
                        <span className={classes["new-badge"]}>New</span>
                      )}
                      {isEditing ? (
                        <div>
                          <textarea
                            className={`${classes["editable-text"]} isEditing`}
                            value={updatedQuestion}
                            onChange={(e) => setUpdatedQuestion(e.target.value)}
                            rows={2}
                          />
                          <div className={classes["icon-container"]}>
                            <p> Answer:</p>
                            <textarea
                              className={`${classes["editable-text"]} isEditing`}
                              value={updatedAnswer}
                              onChange={(e) => setUpdatedAnswer(e.target.value)}
                            />
                          </div>
                          <div className={classes["icon-container"]}>
                            <p className={classes["editable-text"]}>
                              Category: {question.reference}
                            </p>
                          </div>

                          <div className={classes["icon-container"]}>
                            <button
                              type="button"
                              className={`${util.btn} ${util["btn--ghost"]} ${util["btn-sm"]}`}
                              onClick={() => {
                                setEditQuestion(null);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className={`${util.btn} ${util["btn--primaryGradient"]} ${util["btn-sm"]}`}
                              onClick={() => {
                                dispatch(
                                  editFfQuestion({
                                    question_id: question.question_id,
                                    updatedQuestion: updatedQuestion,
                                    answer: updatedAnswer,
                                  })
                                );
                                setEditQuestion(null);
                              }}
                            >
                              Update
                            </button>
                          </div>
                        </div>
                      ) : question.selected ? (
                        <div>
                          <div className={classes.alignItem}>
                            <div className={classes["question-content"]}>
                              <p style={{ width: "80%" }}>{boldTextDetector(question.question)}</p>
                              {/* Render images if present - need to upgrade logic to support answer images */}
                              {question.images && question.images.length > 0 && (
                                <img
                                  key={question.images[0].id || 0}
                                  src={question.images[0].data_url || question.images[0]}
                                  alt={question.images[0].renderer || question.images[0].id || `question-image-0`}
                                  className={classes["question-image"]}
                                />
                              )}
                            </div>
                            <div>
                              <FontAwesomeIcon
                                className={getIconStyle(question?.edited)}
                                icon={faEdit}
                                onClick={() => {
                                  setEditQuestion(question.question_id + "FF");
                                  setUpdatedQuestion(question.question);
                                  setUpdatedAnswer(question.answer);
                                }}
                              />
                            </div>
                          </div>

                          <div>
                            <p>Answer:</p>
                            {question.images && question.images.length > 1 && (
                              <img
                                src={question.images[1].data_url || question.images[1]}
                                alt={question.images[1].renderer || question.images[1].id || `answer-image`}
                                className={classes["question-image"]}
                              />
                            )}
                            <p>{question.answer}</p>
                          </div>
                          <div className={classes.alignItem}>
                            {(!question.images || question.images.length === 0) ? (
                              <p>Category: {boldTextDetector(question.reference)}</p>
                            ) : (
                              <p></p>
                            )}
                            <div className={classes["icon-container"]}>
                              <FontAwesomeIcon
                                className={`${classes.icon} ${question?.comments
                                  ? classes["icon-color"]
                                  : ""
                                  }`}
                                icon={faCommentDots}
                                onClick={() => {
                                  setCurrentQuestion({
                                    question,
                                    type: "FF",
                                  });
                                  setComment(question?.comments || "");
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className={classes.alignItem}>
                            <p style={{ width: "80%" }} className={classes.textMuted}>
                              {boldTextDetector(question.question)}
                            </p>
                          </div>
                        </div>
                      )}
                      {getCommentFieldModal(question?.question_id, "FF")}
                    </div>
                  </div>
                  <div style={{ paddingTop: "20px" }} />
                </div>
              </div>
            );
          })}
          {filteredTFQuestions?.length > 0 && (
            <h2 className={`${classes.title} ${classes.multipleSpacer}`}>
              <br />
              True-False Questions
            </h2>
          )}
          {filteredTFQuestions?.map((question, index) => {
            const isEditing = editQuestion === question.question_id + "TF";
            return (
              <div
                key={question?.question_id || index}
                className={classes.question}
                data-testid="question-card"
                data-question-type="tf"
                data-question-id={question?.question_id}
              >
                {getSelectBox(question.selected, () =>
                  tfQuestionSelectedHandler(question.question_id)
                )}
                <div className={classes.fullWidth}>
                  <div className={classes["content-wrapper"]}>
                    <div
                      className={`${question.selected
                        ? classes["paragraph-box"]
                        : classes["paragraph-box-unselect"]
                        } ${isEditing ? classes["editing"] : ""}`}
                    >
                      {question.new && (
                        <span className={classes["new-badge"]}>New</span>
                      )}
                      {isEditing ? (
                        <div>
                          <textarea
                            className={`${classes["editable-text"]} isEditing`}
                            value={updatedQuestion}
                            onChange={(e) => setUpdatedQuestion(e.target.value)}
                            rows={2}
                          />



                          <div className={classes["icon-container"]}>
                            <p> Answer:</p>
                            <textarea
                              maxLength={5}
                              className={`${classes["editable-text"]} isEditing`}
                              value={updatedAnswer}
                              onChange={tfAnswerUpdateHandler}
                            />
                          </div>

                          <div className={classes["icon-container"]}>
                            <p className={classes["editable-text"]}>
                              {" "}
                              Category: {question.reference}
                            </p>
                          </div>

                          {errMsg ? (
                            <div className={classes.errMsg}>{errMsg}</div>
                          ) : null}

                          <div className={classes["icon-container"]}>
                            <button
                              type="button"
                              className={`${util.btn} ${util["btn--ghost"]} ${util["btn-sm"]}`}
                              onClick={() => {
                                setEditQuestion(null);
                                setErrMsg("");
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className={`${util.btn} ${util["btn--primaryGradient"]} ${util["btn-sm"]}`}
                              onClick={() => tfQuestionUpdateHandler(question)}
                            >
                              Update
                            </button>
                          </div>
                        </div>
                      ) : question.selected ? (
                        <div>
                          <div>
                            <div className={classes.alignItem}>
                              <p style={{ width: "80%" }}>
                                {boldTextDetector(question.question)}
                              </p>
                              <div>
                                <FontAwesomeIcon
                                  className={getIconStyle(question?.edited)}
                                  icon={faEdit}
                                  onClick={() => {
                                    setEditQuestion(
                                      question.question_id + "TF"
                                    );
                                    setUpdatedQuestion(question.question);
                                    setUpdatedOptions(question.options);
                                    setUpdatedAnswer(question.answer);
                                  }}
                                />
                              </div>
                            </div>
                            <div style={{ lineHeight: "25px" }}>
                              <p>{boldTextDetector(question.options.T)}</p>
                              <p>{boldTextDetector(question.options.F)}</p>
                            </div>
                            
                            <div>
                              <p>Answer:</p>
                              {question.answerImages && question.answerImages.length > 0 && (
                                question.answerImages.map((img, idx) => (
                                  <img
                                    key={img.id || idx}
                                    src={img.data_url}
                                    alt={img.renderer || img.id || `answer-image-${idx}`}
                                    className={classes["question-image"]}
                                  />
                                ))
                              )}
                              <p>{question.answer}</p>
                            </div>
                            <div className={classes.alignItem}>
                              <p>Category: {boldTextDetector(question.reference)}</p>
                              <div className={classes["icon-container"]}>
                                <FontAwesomeIcon
                                  className={getIconStyle(question?.comments)}
                                  icon={faCommentDots}
                                  onClick={() => {
                                    setCurrentQuestion({
                                      question,
                                      type: "TF",
                                    });
                                    setComment(question?.comments || "");
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className={classes.alignItem}>
                            <p style={{ width: "80%" }} className={classes.textMuted}>
                              {question.question}
                            </p>
                          </div>
                        </div>
                      )}
                      {getCommentFieldModal(question?.question_id, "TF")}
                    </div>
                  </div>
                  <div style={{ paddingTop: "20px" }} />
                </div>
              </div>
            );
          })}
          {filteredFBQuestions?.length > 0 && (
            <h2 className={`${classes.title} ${classes.multipleSpacer}`}>
              <br />
              Fill in the Blank Questions
            </h2>
          )}
          {filteredFBQuestions?.map((question, index) => {
            const isEditing = editQuestion === question.question_id + "FB";
            return (
              <div
                key={question?.question_id || index}
                className={classes.question}
                data-testid="question-card"
                data-question-type="fb"
                data-question-id={question?.question_id}
              >
                {getSelectBox(question.selected, () =>
                  fbQuestionSelectedHandler(question.question_id)
                )}
                <div className={classes.fullWidth}>
                  <div className={classes["content-wrapper"]}>
                    <div
                      className={`${question.selected
                        ? classes["paragraph-box"]
                        : classes["paragraph-box-unselect"]
                        } ${isEditing ? classes["editing"] : ""}`}
                    >
                      {question.new && (
                        <span className={classes["new-badge"]}>New</span>
                      )}
                      {isEditing ? (
                        <div>
                          <textarea
                            className={`${classes["editable-text"]} isEditing`}
                            value={updatedQuestion}
                            onChange={(e) => setUpdatedQuestion(e.target.value)}
                            rows={2}
                          />




                          <div className={classes["icon-container"]}>
                            <p> Answer:</p>
                            <textarea
                              className={`${classes["editable-text"]} isEditing`}
                              value={updatedAnswer}
                              onChange={(e) => setUpdatedAnswer(e.target.value)}
                            />
                          </div>

                          <div className={classes["icon-container"]}>
                            <p className={classes["editable-text"]}>
                              {" "}
                              Category: {question.reference}
                            </p>
                          </div>

                          {errMsg ? (
                            <div className={classes.errMsg}>{errMsg}</div>
                          ) : null}

                          <div className={classes["icon-container"]}>
                            <button
                              type="button"
                              className={`${util.btn} ${util["btn--ghost"]} ${util["btn-sm"]}`}
                              onClick={() => {
                                setEditQuestion(null);
                                setErrMsg("");
                              }}
                            >
                              Cancel
                            </button>

                            <button
                              type="button"
                              className={`${util.btn} ${util["btn--primaryGradient"]} ${util["btn-sm"]}`}
                              onClick={() => {
                                dispatch(
                                  editFbQuestion({
                                    question_id: question.question_id,
                                    updatedQuestion: updatedQuestion,
                                    answer: updatedAnswer,
                                  })
                                );
                                setEditQuestion(null);
                              }}
                            >
                              Update
                            </button>
                          </div>
                        </div>
                      ) : question.selected ? (
                        <div>
                          <div>
                            <div className={classes.alignItem}>
                              <p style={{ width: "80%" }}>
                                {boldTextDetector(question.question)}
                              </p>
                              <div>
                                <FontAwesomeIcon
                                  className={getIconStyle(question?.edited)}
                                  icon={faEdit}
                                  onClick={() => {
                                    setEditQuestion(
                                      question.question_id + "FB"
                                    );
                                    setUpdatedQuestion(question.question);
                                    setUpdatedAnswer(question.answer);
                                  }}
                                />
                              </div>
                            </div>
                            
                            <div>
                              <p>Answer:</p>
                              {question.answerImages && question.answerImages.length > 0 && (
                                question.answerImages.map((img, idx) => (
                                  <img
                                    key={img.id || idx}
                                    src={img.data_url}
                                    alt={img.renderer || img.id || `answer-image-${idx}`}
                                    className={classes["question-image"]}
                                  />
                                ))
                              )}
                              <p>{question.answer}</p>
                            </div>
                            <div className={classes.alignItem}>
                              <p>Category: {boldTextDetector(question.reference)}</p>
                              <div className={classes["icon-container"]}>
                                <FontAwesomeIcon
                                  className={getIconStyle(question?.comments)}
                                  icon={faCommentDots}
                                  onClick={() => {
                                    setCurrentQuestion({
                                      question,
                                      type: "FB",
                                    });
                                    setComment(question?.comments || "");
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className={classes.alignItem}>
                            <p style={{ width: "80%" }} className={classes.textMuted}>
                              {question.question}
                            </p>
                          </div>
                        </div>
                      )}
                      {getCommentFieldModal(question?.question_id, "FB")}
                    </div>
                  </div>
                  <div style={{ paddingTop: "20px" }} />
                </div>
              </div>
            );
          })}

          <div className={classes.userExperienceMain}>
            <div className={classes.userExperienceEmptyDiv}></div>
            <div className={classes.fullWidth}>
              <h2 className={`${classes.title}`}>
                <br />
                Tell us about your experience
              </h2>
              <div
                className={`${classes.question}`}
                style={{ position: "relative" }}
              >
                <textarea
                  rows={4}
                  className={classes.userExperience}
                  value={userFeedback}
                  onChange={(e) => setUserFeedback(e.target.value)}
                  placeholder={
                    isFeedbackSent
                      ? afterSentFeedbackPlaceHolder
                      : feedbackPlaceholder
                  }
                />
                <FontAwesomeIcon
                  className={`${classes.icon} ${classes.planeIcon}`}
                  icon={faPaperPlane}
                  onClick={feedbackSendHandler}
                />
              </div>
            </div>
          </div>

          <div className={`${classes['form-actions']} ${classes['form-actions--terminal']}`}>
            <ActionButton
              text="Generate more questions"
              subText="(Your current questions will be saved)"
              handleOnClick={generateMoreQuestionsHandler}
              variant="secondary"
            />
            <ActionButton
              text="Export to Google Docs, Google Forms, Kahoot, or Blooket"
              subText="(A ready-to-use exported file will download or open in a new tab. You can come back to this page at any time)"
              variant="secondary"
              handleOnClick={exportHandler}
            />
            <ActionButton
              text="Back to Home"
              variant="secondary"
              handleOnClick={backToHomeHandler}
            />
          </div>
        </div>
  </section>

      {/* Google drive permission Modal */}
      <SignInInfoModal
        isOpen={infoModal}
        cancelHandler={() => setInfoModal(false)}
        continueHandler={signInInfoContinueHandler}
        error={exportError}
      />

      {/* Export Button Modal */}
      <ExportModal
        isOpen={exportModal}
        backHandler={backBtnExportModalHandler}
        continueHandler={continueBtnExportModalHandler}
        exportType={exportType}
        exportTypeHandler={({ value }) => setExportType(value)}
        exportCount={{
          passage: isPassageSelected,
          mcCount: filteredMCQuestions?.filter((question) => question.selected)
            ?.length,
          ffCount: filteredFFQuestions?.filter((question) => question.selected)
            ?.length,
          tfCount: filteredTFQuestions?.filter((question) => question.selected)
            ?.length,
          fbCount: filteredFBQuestions?.filter((question) => question.selected)
            ?.length,
        }}
        submitForSharingHandler={(value) => setSubmittedForSharing(value)}
        submitForSharingDescription={submitForSharingDescription}
        setSubmitForSharingDescription={setSubmitForSharingDescription}
      />

      {/* Export Extra Info Modal */}
      <ExportExtraInfoModal
        isOpen={exportExtraInfoModal}
        backHandler={backBtnExportExtraInfoModalHandler}
        continueHandler={continueBtnExportExtraInfoModalHandler}
        stepSkipHandler={stepSkipBtnExportExtraInfoModalHandler}
        exportType={exportType}
        googleFormExtraInfo={googleFormExtraInfo}
        setGoogleFormExtraInfo={setGoogleFormExtraInfo}
      />

      {/* Back To Home Modal */}
      <ModalWrapper
        modalProps={{
          isOpen: backToHomeModal,
          description: `Are you sure you want to leave this page? Your current work will be lost.`,
          leftBtnHandler: backToHomeCancelBtnHandler,
          rightBtnHandler: backToHomeConfirmBtnHandler,
          buttons: {
            leftBtn: "Cancel",
            rightBtn: "Confirm",
          },
        }}
      />

      {/* Issue with upload modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        titleText={"Potential issue with upload"}
        bodyText={fileUploadMessage}
      />
    </>
  );
}

export default QuestionsReview;
